import React, { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import ErrorDialog from "../components/ErrorDialog";

interface ErrorDialogState {
  open: boolean;
  title?: string;
  message: string;
  actionButtonText?: string;
}

interface ErrorDialogContextType {
  showErrorDialog: (options: {
    title?: string;
    message: string;
    actionButtonText?: string;
  }) => void;
  hideErrorDialog: () => void;
}

const ErrorDialogContext = createContext<ErrorDialogContextType | undefined>(
  undefined
);

interface ErrorDialogProviderProps {
  children: ReactNode;
}

export const ErrorDialogProvider: React.FC<ErrorDialogProviderProps> = ({
  children,
}) => {
  const [dialogState, setDialogState] = useState<ErrorDialogState>({
    open: false,
    title: "",
    message: "",
    actionButtonText: "OK",
  });

  const showErrorDialog = ({
    title = "Error",
    message,
    actionButtonText = "OK",
  }: {
    title?: string;
    message: string;
    actionButtonText?: string;
  }) => {
    setDialogState({
      open: true,
      title,
      message,
      actionButtonText,
    });
  };

  const hideErrorDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const contextValue: ErrorDialogContextType = {
    showErrorDialog,
    hideErrorDialog,
  };

  return (
    <ErrorDialogContext.Provider value={contextValue}>
      {children}
      <ErrorDialog
        open={dialogState.open}
        onClose={hideErrorDialog}
        title={dialogState.title}
        message={dialogState.message}
        actionButtonText={dialogState.actionButtonText}
      />
    </ErrorDialogContext.Provider>
  );
};

export const useErrorDialog = (): ErrorDialogContextType => {
  const context = useContext(ErrorDialogContext);
  if (context === undefined) {
    throw new Error(
      "useErrorDialog must be used within an ErrorDialogProvider"
    );
  }
  return context;
};
